<template>
  <div class="company-default-stats">
    <v-progress-linear v-if="loading" indeterminate color="primary" height="2" />

    <v-tabs v-model="tab" background-color="primary" dark>
      <v-tab v-for="item in tabDefs" :key="item.key">
        <v-icon style="margin-right: 7px">{{ item.icon }}</v-icon>
        {{ item.label }}
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" :touchless="true">
      <v-tab-item>
        <div class="px-2 mt-2">
          <meals-meals-chart :orders="orders" />
        </div>
      </v-tab-item>
      <v-tab-item>
        <div class="px-2 mt-2">
          <meals-shifts-chart :orders="orders" />
        </div>
      </v-tab-item>
      <v-tab-item>
        <div class="px-2 mt-2">
          <meals-taken-chart :orders="orders" />
        </div>
      </v-tab-item>
      <v-tab-item>
        <div class="px-2 mt-2">
          <meals-companies-chart :orders="orders" />
        </div>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import reportService from "@/services/report";
import companyService from "@/services/company";
import MealsMealsChart from "@/components/statistics/meals/MealsMealsChart.vue";
import MealsShiftsChart from "@/components/statistics/meals/MealsShiftsChart.vue";
import MealsTakenChart from "@/components/statistics/meals/MealsTakenChart.vue";
import MealsCompaniesChart from "@/components/statistics/meals/MealsCompaniesChart.vue";

export default {
  name: "CompanyDefaultStats",
  components: { MealsMealsChart, MealsShiftsChart, MealsTakenChart, MealsCompaniesChart },
  props: {
    date: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      orders: [],
      branches: [], // [[id, name], ...]
      tab: 0,
    };
  },
  computed: {
    tabDefs() {
      return [
        { key: "meals", icon: "mdi-food", label: this.$t("meal_in_meals") },
        { key: "shifts", icon: "mdi-clock-time-four-outline", label: this.$t("orders_by_shift") },
        { key: "taken", icon: "mdi-check-circle-outline", label: this.$t("ordered_taken_meals") },
        { key: "branches", icon: "mdi-store", label: this.$t("branches") },
      ];
    },
    branchNameMap() {
      const map = {};
      for (const b of this.branches) {
        map[String(b[0])] = this.toLatin(b[1]);
      }
      return map;
    },
  },
  methods: {
    async fetchBranches() {
      const companyId = this.$store.state.company?.id;
      if (!companyId) return;
      try {
        const { data } = await companyService.getBranches({ companies: companyId });
        this.branches = Array.isArray(data) ? data : [];
      } catch {
        this.branches = [];
      }
    },
    async fetchData() {
      const restaurantId = this.$store.state.company?.restaurant_id;
      const companyId = this.$store.state.company?.id;
      if (!restaurantId || !companyId || !this.date?.length) return;
      this.loading = true;
      try {
        const from = this.date[0];
        const to = this.date[1] || this.date[0];
        // Pass branch IDs so the API returns per-branch data; fall back to parent if no branches.
        const branchIds = this.branches.map((b) => b[0]);
        const companyParam = branchIds.length ? branchIds.join(",") : companyId;
        const res = await reportService.getMealsForRestaurant(restaurantId, {
          company: companyParam,
          date: from,
          date2: to,
        });
        this.orders = this.parseOrders(res.data || {});
      } catch {
        this.orders = [];
      } finally {
        this.loading = false;
      }
    },
    // raw: { companyNameOrId: { "1": { mealName: { id, quantity, quantity_payed } } } }
    parseOrders(raw) {
      const result = [];
      for (const key in raw) {
        // API may key by company name or numeric ID — resolve to a readable name
        const companyName = this.branchNameMap[key] || this.toLatin(key);
        const shifts = raw[key];
        for (const shiftStr in shifts) {
          const shift = parseInt(shiftStr);
          if (isNaN(shift)) continue;
          const meals = shifts[shiftStr];
          for (const mealName in meals) {
            const meal = meals[mealName];
            result.push({
              company: companyName,
              meal: this.toLatin(mealName),
              category_name: this.toLatin(mealName),
              shift,
              qty: meal.quantity || 0,
              quantity: meal.quantity || 0,
              taken: meal.quantity_payed || 0,
            });
          }
        }
      }
      return result;
    },
  },
  watch: {
    date: { handler() { this.fetchData(); }, deep: true },
  },
  async created() {
    await this.fetchBranches();
    this.fetchData();
  },
};
</script>

<style scoped>
.company-default-stats ::v-deep .v-tabs-items,
.company-default-stats ::v-deep .v-window__container,
.company-default-stats ::v-deep .v-window-item {
  overflow: visible !important;
}
</style>
